import { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Minus,
  Activity,
  Users,
  AlertTriangle,
  ChevronDown,
  RefreshCw,
  BarChart3,
  FileText,
  Target,
  CheckCircle,
  Percent,
  ShieldAlert,
  MonitorPlay,
  UserX,
  Search,
} from 'lucide-react';
import {
  analyticsApi,
  examsApi,
} from '@/api/endpoints';
import { Card, CardHeader } from '@azyntor-labs/nexus-ui';
import { useNavigate } from 'react-router-dom';
import type {
  TrendAnalysisData,
  PerformanceTrendData,
  ViolationHeatmapData,
  ExamComparisonData,
  StudentRiskProfile,
  LiveExamStats,
} from '@/api/endpoints';

type TimeRange = '7d' | '30d' | '90d';

const timeRangeToDays: Record<TimeRange, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const formatTrendValue = (value: number, metric: string): string => {
  if (metric.toLowerCase().includes('rate') || metric.toLowerCase().includes('percentage')) {
    return `${value.toFixed(1)}%`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return value.toFixed(1);
};

// Helper to safely extract array data from various API response shapes
function extractArray<T>(response: any): T[] {
  if (!response) return [];
  // response from axios: { data: ApiResponse<T[]> } where ApiResponse = { data: T[] }
  const inner = response?.data?.data;
  if (Array.isArray(inner)) return inner;
  // Sometimes the inner data is itself wrapped: { data: T[] }
  if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;
  if (inner && typeof inner === 'object' && Array.isArray(inner.items)) return inner.items;
  // Direct array
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

export const EnhancedAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [selectedExamIds, setSelectedExamIds] = useState<string[]>([]);
  const [examModalOpen, setExamModalOpen] = useState(false);
  const [examSearchQuery, setExamSearchQuery] = useState('');
  // dropdownRef removed — using modal instead

  const timeRangeDays = timeRangeToDays[timeRange];

  // Lock body scroll when exam modal is open + Escape to close
  useEffect(() => {
    if (examModalOpen) {
      document.body.style.overflow = 'hidden';
      const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setExamModalOpen(false); };
      document.addEventListener('keydown', handleEsc);
      return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handleEsc); };
    }
  }, [examModalOpen]);

  // Trend Analysis
  const { data: trendData, isLoading: loadingTrends } = useQuery({
    queryKey: ['trend-analysis', timeRange],
    queryFn: () => analyticsApi.getTrendAnalysis({ period: timeRange }),
    staleTime: 60000,
  });

  // Performance Trends
  const { data: performanceData, isLoading: loadingPerformance } = useQuery({
    queryKey: ['performance-trends', timeRangeDays],
    queryFn: () => analyticsApi.getPerformanceTrends({ days: timeRangeDays }),
    staleTime: 60000,
  });

  // Violation Heatmap
  const { data: heatmapData, isLoading: loadingHeatmap } = useQuery({
    queryKey: ['violation-heatmap', timeRangeDays],
    queryFn: () => analyticsApi.getViolationHeatmap({ days: timeRangeDays }),
    staleTime: 60000,
  });

  // Exam list for comparison dropdown
  const { data: examsListData } = useQuery({
    queryKey: ['exams-list-for-comparison'],
    queryFn: () => examsApi.list({ per_page: 100 }),
    staleTime: 120000,
  });

  // Exam Comparison
  const { data: comparisonData, isLoading: loadingComparison } = useQuery({
    queryKey: ['exam-comparison', selectedExamIds],
    queryFn: () => analyticsApi.getExamComparison(selectedExamIds),
    enabled: selectedExamIds.length > 0,
    staleTime: 60000,
  });

  // Live Exam Stats
  const { data: liveStatsData, isLoading: loadingLive } = useQuery({
    queryKey: ['live-exam-stats'],
    queryFn: () => analyticsApi.getLiveExamStats(),
    refetchInterval: 30000,
    staleTime: 15000,
  });

  // Student Risk Profiles
  const { data: riskData, isLoading: loadingRisk } = useQuery({
    queryKey: ['student-risk-profiles'],
    queryFn: () => analyticsApi.getStudentRiskProfiles(),
    staleTime: 60000,
  });

  // Extract data with safe fallbacks
  const trends: TrendAnalysisData[] = extractArray<TrendAnalysisData>(trendData);
  const performanceTrends: PerformanceTrendData[] = extractArray<PerformanceTrendData>(performanceData);
  const heatmapRaw: ViolationHeatmapData[] = extractArray<ViolationHeatmapData>(heatmapData);

  // Extract exam list — handle { data: { exams: [] } } or { data: [] } or { data: { data: [] } }
  const examsListRaw = examsListData?.data?.data;
  const examsList: any[] = (() => {
    if (Array.isArray(examsListRaw)) return examsListRaw;
    if (examsListRaw && typeof examsListRaw === 'object') {
      if (Array.isArray((examsListRaw as any).exams)) return (examsListRaw as any).exams;
      if (Array.isArray((examsListRaw as any).data)) return (examsListRaw as any).data;
      if (Array.isArray((examsListRaw as any).items)) return (examsListRaw as any).items;
    }
    if (Array.isArray(examsListData?.data)) return examsListData.data as any[];
    return [];
  })();

  const comparisonResults: ExamComparisonData[] = extractArray<ExamComparisonData>(comparisonData);
  const liveStats: LiveExamStats[] = extractArray<LiveExamStats>(liveStatsData);
  const riskProfiles: StudentRiskProfile[] = extractArray<StudentRiskProfile>(riskData);

  // ---- Stat card computed values ----
  const statCards = useMemo(() => {
    // From comparison data (all exams if available) or performance trends
    const totalExams = comparisonResults.length > 0
      ? comparisonResults.length
      : examsList.length;

    const totalAttempts = comparisonResults.reduce((sum, e) => sum + (e.total_attempts || 0), 0);

    const avgScore = comparisonResults.length > 0
      ? comparisonResults.reduce((sum, e) => sum + (e.average_score || 0), 0) / comparisonResults.length
      : performanceTrends.length > 0
        ? performanceTrends.reduce((sum, p) => sum + (p.average_score || 0), 0) / performanceTrends.length
        : 0;

    const passRate = comparisonResults.length > 0
      ? comparisonResults.reduce((sum, e) => sum + (e.pass_rate || 0), 0) / comparisonResults.length
      : performanceTrends.length > 0
        ? performanceTrends.reduce((sum, p) => sum + (p.pass_rate || 0), 0) / performanceTrends.length
        : 0;

    const completionRate = comparisonResults.length > 0
      ? comparisonResults.reduce((sum, e) => sum + (e.completion_rate || 0), 0) / comparisonResults.length
      : 0;

    const totalViolations = comparisonResults.length > 0
      ? comparisonResults.reduce((sum, e) => sum + Math.round((e.violation_rate || 0) * (e.total_attempts || 0) / 100), 0)
      : performanceTrends.reduce((sum, p) => sum + (p.violations || 0), 0);

    const activeExams = liveStats.length;

    const atRiskStudents = riskProfiles.filter(
      (r) => r.risk_level === 'high' || r.risk_level === 'critical'
    ).length;

    // Build trend lookup from trends data
    const trendMap: Record<string, { change: number; direction: string }> = {};
    trends.forEach((t) => {
      trendMap[t.metric_name] = { change: t.percentage_change, direction: t.trend };
    });

    return [
      {
        label: 'Total Exams',
        value: totalExams.toString(),
        icon: FileText,
        color: 'text-teal-400',
        bg: 'bg-teal-500/10',
        trend: null as { change: number; direction: string } | null,
      },
      {
        label: 'Total Attempts',
        value: totalAttempts >= 1000 ? `${(totalAttempts / 1000).toFixed(1)}k` : totalAttempts.toString(),
        icon: Target,
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
        trend: trendMap['attempts'] || null,
      },
      {
        label: 'Avg Score',
        value: `${avgScore.toFixed(1)}`,
        icon: BarChart3,
        color: 'text-blue-400',
        bg: 'bg-blue-500/10',
        trend: trendMap['average_score'] || null,
      },
      {
        label: 'Pass Rate',
        value: `${passRate.toFixed(1)}%`,
        icon: CheckCircle,
        color: 'text-purple-400',
        bg: 'bg-purple-500/10',
        trend: trendMap['pass_rate'] || null,
      },
      {
        label: 'Completion',
        value: `${completionRate.toFixed(1)}%`,
        icon: Percent,
        color: 'text-cyan-400',
        bg: 'bg-cyan-500/10',
        trend: null,
      },
      {
        label: 'Violations',
        value: totalViolations.toString(),
        icon: ShieldAlert,
        color: 'text-amber-400',
        bg: 'bg-amber-500/10',
        trend: trendMap['violations'] || null,
      },
      {
        label: 'Active Exams',
        value: activeExams.toString(),
        icon: MonitorPlay,
        color: 'text-emerald-400',
        bg: 'bg-emerald-500/10',
        trend: null,
      },
      {
        label: 'At-Risk',
        value: atRiskStudents.toString(),
        icon: UserX,
        color: 'text-red-400',
        bg: 'bg-red-500/10',
        trend: null,
      },
    ];
  }, [comparisonResults, examsList, performanceTrends, liveStats, riskProfiles, trends]);

  // Build violation data aggregated by day of week for compact bar chart
  const violationsByDay = useMemo(() => {
    const dayCounts: Record<number, number> = {};
    for (let d = 0; d < 7; d++) dayCounts[d] = 0;
    for (const h of heatmapRaw) {
      dayCounts[h.day_of_week] = (dayCounts[h.day_of_week] || 0) + h.count;
    }
    return DAYS_OF_WEEK.map((name, i) => ({
      day: name,
      violations: dayCounts[i] || 0,
    }));
  }, [heatmapRaw]);

  // Build compact heatmap grid (still available for tooltip detail)
  const heatmapGrid = useMemo(() => {
    const maxCount = Math.max(1, ...heatmapRaw.map((h) => h.count));
    const grid: { hour: number; day: number; count: number; opacity: number }[][] = [];

    for (let hour = 0; hour < 24; hour++) {
      const row: { hour: number; day: number; count: number; opacity: number }[] = [];
      for (let day = 0; day < 7; day++) {
        const entry = heatmapRaw.find((h) => h.hour === hour && h.day_of_week === day);
        const count = entry?.count || 0;
        row.push({
          hour,
          day,
          count,
          opacity: count / maxCount,
        });
      }
      grid.push(row);
    }
    return grid;
  }, [heatmapRaw]);

  const toggleExamSelection = (examId: string) => {
    setSelectedExamIds((prev) =>
      prev.includes(examId)
        ? prev.filter((id) => id !== examId)
        : [...prev, examId]
    );
  };

  // Filtered exams for dropdown search
  const filteredExams = useMemo(() => {
    if (!examSearchQuery.trim()) return examsList;
    const q = examSearchQuery.toLowerCase();
    return examsList.filter((exam: any) =>
      (exam.title || '').toLowerCase().includes(q)
    );
  }, [examsList, examSearchQuery]);

  const allFilteredSelected = filteredExams.length > 0 && filteredExams.every((e: any) => selectedExamIds.includes(e.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      // Deselect all filtered exams
      const filteredIds = new Set(filteredExams.map((e: any) => e.id));
      setSelectedExamIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    } else {
      // Select all filtered exams
      const newIds = new Set([...selectedExamIds, ...filteredExams.map((e: any) => e.id)]);
      setSelectedExamIds(Array.from(newIds));
    }
  };

  const isAnyLoading = loadingTrends || loadingPerformance || loadingHeatmap || loadingLive || loadingRisk;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Enhanced Analytics</h1>
          <p className="text-white/50 mt-1">
            Advanced performance trends, violation heatmaps, and risk profiles
          </p>
        </div>
        <div className="flex items-center space-x-1 bg-white/5 border border-white/[0.06] rounded-lg p-1">
          {(['7d', '30d', '90d'] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                timeRange === range
                  ? 'bg-teal-500 text-white'
                  : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Analytics Navigation Tabs */}
      <div className="flex items-center space-x-1 bg-white/5 border border-white/[0.06] rounded-lg p-1">
        <button
          onClick={() => navigate('/analytics')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5"
        >
          Standard
        </button>
        <button className="px-4 py-2 rounded-lg text-sm font-medium bg-teal-500 text-white">
          Enhanced
        </button>
        <button
          onClick={() => navigate('/analytics/campaigns')}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white hover:bg-white/5"
        >
          Campaigns
        </button>
      </div>

      {/* Section 1: 8 Stat Cards with inline trends */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        {isAnyLoading
          ? Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 animate-pulse"
              >
                <div className="w-7 h-7 rounded-lg bg-white/[0.06] mb-2" />
                <div className="h-3 bg-white/[0.06] rounded w-14 mb-2" />
                <div className="h-6 bg-white/[0.06] rounded w-10" />
              </div>
            ))
          : statCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.label}
                  className="bg-gray-800/50 border border-gray-700 rounded-xl p-3 hover:border-gray-600 transition-colors"
                >
                  <div className={`w-7 h-7 rounded-lg ${card.bg} flex items-center justify-center mb-2`}>
                    <Icon className={`w-3.5 h-3.5 ${card.color}`} />
                  </div>
                  <p className="text-[11px] text-white/50 leading-tight mb-1 truncate">{card.label}</p>
                  <p className="text-lg font-bold text-white/90 leading-none">{card.value}</p>
                  {card.trend && (
                    <div className="flex items-center gap-0.5 mt-1">
                      {card.trend.direction === 'up' ? (
                        <TrendingUp className="w-3 h-3 text-emerald-400" />
                      ) : card.trend.direction === 'down' ? (
                        <TrendingDown className="w-3 h-3 text-red-400" />
                      ) : (
                        <Minus className="w-3 h-3 text-white/30" />
                      )}
                      <span className={`text-[10px] font-medium ${card.trend.direction === 'up' ? 'text-emerald-400' : card.trend.direction === 'down' ? 'text-red-400' : 'text-white/30'}`}>
                        {card.trend.change >= 0 ? '+' : ''}{card.trend.change.toFixed(1)}%
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
      </div>

      {/* Section 2: Performance Trends Chart */}
      <Card>
        <CardHeader
          title="Performance Trends"
          subtitle="Score and pass rate trends over the selected time period"
        />
        {loadingPerformance ? (
          <div className="h-72 flex items-center justify-center text-white/50">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading chart data...
          </div>
        ) : performanceTrends.length === 0 ? (
          <div className="h-72 flex flex-col items-center justify-center text-white/40">
            <BarChart3 className="w-10 h-10 mb-2 opacity-40" />
            <p>No performance data available for this period</p>
            <p className="text-xs text-white/30 mt-1">Try selecting a different time range</p>
          </div>
        ) : (
          <div className="px-2">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                  tickFormatter={(val) => {
                    const d = new Date(val);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                />
                <YAxis
                  tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                  domain={[0, 100]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1a1f2e',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '8px',
                    color: 'rgba(255,255,255,0.9)',
                  }}
                  labelFormatter={(val) => new Date(val).toLocaleDateString()}
                />
                <Legend
                  wrapperStyle={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="average_score"
                  stroke="#14b8a6"
                  strokeWidth={2}
                  dot={false}
                  name="Avg Score"
                />
                <Line
                  type="monotone"
                  dataKey="pass_rate"
                  stroke="#06b6d4"
                  strokeWidth={2}
                  dot={false}
                  name="Pass Rate"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Section 3: Violation Heatmap - Compact */}
      <Card>
        <CardHeader
          title="Violation Heatmap"
          subtitle="Violations by day of week and hour -- darker red indicates higher frequency"
        />
        {loadingHeatmap ? (
          <div className="h-48 flex items-center justify-center text-white/50">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading heatmap...
          </div>
        ) : heatmapRaw.length === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center text-white/40">
            <AlertTriangle className="w-10 h-10 mb-2 opacity-40" />
            <p>No violation data available</p>
            <p className="text-xs text-white/30 mt-1">Violations will appear here once exams are proctored</p>
          </div>
        ) : (
          <div className="px-4 pb-4">
            {/* Compact bar chart: violations by day of week */}
            <div className="mb-4">
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={violationsByDay} barSize={28}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="day"
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: 12,
                    }}
                    formatter={(value: number) => [value, 'Violations']}
                  />
                  <Bar
                    dataKey="violations"
                    fill="rgba(239, 68, 68, 0.7)"
                    radius={[4, 4, 0, 0]}
                    name="Violations"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Compact heatmap grid */}
            <div className="overflow-x-auto" style={{ maxHeight: '200px' }}>
              <div className="min-w-[320px]">
                {/* Day labels header */}
                <div className="flex items-center">
                  <div className="w-8 shrink-0" />
                  {DAYS_OF_WEEK.map((day) => (
                    <div
                      key={day}
                      className="flex-1 text-center text-[10px] font-medium text-white/40 pb-1"
                    >
                      {day}
                    </div>
                  ))}
                </div>
                {/* Hour rows -- show every 2 hours for compactness */}
                {heatmapGrid
                  .filter((_, hour) => hour % 2 === 0)
                  .map((row) => (
                    <div key={row[0].hour} className="flex items-center">
                      <div className="w-8 shrink-0 text-right pr-1 text-[9px] text-white/30">
                        {row[0].hour.toString().padStart(2, '0')}
                      </div>
                      {row.map((cell) => (
                        <div
                          key={`${cell.hour}-${cell.day}`}
                          className="flex-1 p-[1px]"
                        >
                          <div
                            className="w-full rounded-[2px] cursor-default transition-colors"
                            style={{
                              height: '14px',
                              backgroundColor:
                                cell.count > 0
                                  ? `rgba(239, 68, 68, ${Math.max(0.15, cell.opacity)})`
                                  : 'rgba(255, 255, 255, 0.03)',
                            }}
                            title={`${DAYS_OF_WEEK[cell.day]} ${cell.hour}:00 - ${cell.count} violations`}
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                {/* Legend */}
                <div className="flex items-center justify-end gap-1.5 mt-2 pr-1">
                  <span className="text-[9px] text-white/30">Less</span>
                  {[0.03, 0.2, 0.4, 0.6, 0.8, 1.0].map((opacity, i) => (
                    <div
                      key={i}
                      className="w-3 h-3 rounded-[2px]"
                      style={{
                        backgroundColor: i === 0
                          ? 'rgba(255, 255, 255, 0.03)'
                          : `rgba(239, 68, 68, ${opacity})`,
                      }}
                    />
                  ))}
                  <span className="text-[9px] text-white/30">More</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Section 4: Exam Comparison */}
      <Card>
        <div className="flex items-center justify-between p-6 pb-0">
          <div>
            <h2 className="text-lg font-semibold text-white">Exam Comparison</h2>
            <p className="text-sm text-white/50 mt-0.5">Select exams to compare performance metrics side by side</p>
          </div>
          <button
            onClick={() => { setExamModalOpen(true); setExamSearchQuery(''); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white/5 border border-white/[0.06] text-white/80 hover:bg-white/[0.08] hover:border-white/[0.12] transition-all"
          >
            {selectedExamIds.length === 0
              ? 'Select Exams'
              : `${selectedExamIds.length} exam${selectedExamIds.length > 1 ? 's' : ''} selected`}
            <ChevronDown className="w-4 h-4" />
          </button>

          {/* Exam Selection Modal — rendered via portal to avoid parent overflow clipping */}
          {examModalOpen && ReactDOM.createPortal(
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)', padding: '16px' }}
              onClick={() => setExamModalOpen(false)}
            >
              <div
                style={{ background: '#1a1f2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', width: '100%', maxWidth: '500px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px rgba(0,0,0,0.5)' }}
                onClick={(e) => e.stopPropagation()}
                onWheel={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div style={{ padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#fff' }}>Select Exams to Compare</h3>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>{selectedExamIds.length} selected</p>
                  </div>
                  <button onClick={() => setExamModalOpen(false)} style={{ padding: '8px', borderRadius: '8px', border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: '18px' }}>✕</button>
                </div>

                {/* Search */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
                  <div style={{ position: 'relative' }}>
                    <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px', color: 'rgba(255,255,255,0.3)' }} />
                    <input
                      type="text"
                      placeholder="Search exams..."
                      value={examSearchQuery}
                      onChange={(e) => setExamSearchQuery(e.target.value)}
                      autoFocus
                      style={{ width: '100%', paddingLeft: '40px', paddingRight: '16px', paddingTop: '10px', paddingBottom: '10px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', fontSize: '14px', color: 'rgba(255,255,255,0.9)', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>
                </div>

                {/* Select All */}
                <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAll} style={{ accentColor: '#14b8a6' }} />
                    <span style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>Select All ({filteredExams.length})</span>
                  </label>
                  {selectedExamIds.length > 0 && (
                    <button onClick={() => setSelectedExamIds([])} style={{ fontSize: '12px', color: '#14b8a6', background: 'none', border: 'none', cursor: 'pointer' }}>Clear all</button>
                  )}
                </div>

                {/* Exam List — scrollable with fixed max height */}
                <div style={{ overflowY: 'scroll', maxHeight: '400px' }}>
                  {filteredExams.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', fontSize: '14px', color: 'rgba(255,255,255,0.4)' }}>
                      {examSearchQuery ? 'No exams match your search' : 'No exams available'}
                    </div>
                  ) : (
                    filteredExams.map((exam: any) => (
                      <label key={exam.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.03)' }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <input type="checkbox" checked={selectedExamIds.includes(exam.id)} onChange={() => toggleExamSelection(exam.id)} style={{ accentColor: '#14b8a6', flexShrink: 0 }} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exam.title}</span>
                          {exam.status && <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)' }}>{exam.status}</span>}
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {/* Footer */}
                <div style={{ padding: '16px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                  <button onClick={() => setExamModalOpen(false)} style={{ padding: '8px 20px', fontSize: '14px', fontWeight: 500, color: '#0a0a14', background: '#14b8a6', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Done</button>
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>

        <div className="p-6 pt-4">
          {selectedExamIds.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-white/40">
              <BarChart3 className="w-10 h-10 mb-2 opacity-40" />
              <p>Select exams above to compare their performance metrics</p>
            </div>
          ) : loadingComparison ? (
            <div className="h-64 flex items-center justify-center text-white/50">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              Loading comparison...
            </div>
          ) : comparisonResults.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-white/40">
              <BarChart3 className="w-10 h-10 mb-2 opacity-40" />
              <p>No comparison data available for the selected exams</p>
              <p className="text-xs text-white/30 mt-1">Selected exams may not have any attempts yet</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={comparisonResults}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="exam_title"
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }}
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 12 }}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '8px',
                      color: 'rgba(255,255,255,0.9)',
                    }}
                  />
                  <Legend
                    wrapperStyle={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}
                  />
                  <Bar dataKey="average_score" fill="#14b8a6" name="Avg Score" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="pass_rate" fill="#06b6d4" name="Pass Rate" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completion_rate" fill="#8b5cf6" name="Completion Rate" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>

              {/* Comparison data table */}
              <div className="mt-6 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">Exam</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Attempts</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Avg Score</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Pass Rate</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Completion</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-white/50 uppercase">Violations</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {comparisonResults.map((exam) => (
                      <tr key={exam.exam_id} className="hover:bg-white/[0.02]">
                        <td className="py-3 px-4 text-white/90 font-medium">{exam.exam_title}</td>
                        <td className="py-3 px-4 text-right text-white/60">{exam.total_attempts}</td>
                        <td className="py-3 px-4 text-right text-white/90">{exam.average_score?.toFixed(1)}%</td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-medium ${
                            exam.pass_rate >= 70 ? 'text-emerald-400' : exam.pass_rate >= 50 ? 'text-amber-400' : 'text-red-400'
                          }`}>
                            {exam.pass_rate?.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-white/60">{exam.completion_rate?.toFixed(1)}%</td>
                        <td className="py-3 px-4 text-right">
                          <span className={exam.violation_rate > 0 ? 'text-red-400' : 'text-white/60'}>
                            {exam.violation_rate?.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Section 5: Live Exam Stats */}
      <Card>
        <div className="flex items-center justify-between p-6 pb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Live Exam Stats</h2>
            <p className="text-sm text-white/50 mt-0.5">Real-time monitoring of active exams</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/40">
            <Activity className="w-3.5 h-3.5 text-emerald-400" />
            Auto-refreshing every 30s
          </div>
        </div>

        <div className="px-6 pb-6">
          {loadingLive ? (
            <div className="h-32 flex items-center justify-center text-white/50">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              Loading live stats...
            </div>
          ) : liveStats.length === 0 ? (
            <div className="h-32 flex flex-col items-center justify-center text-white/40">
              <Activity className="w-10 h-10 mb-2 opacity-40" />
              <p>No active exams currently running</p>
              <p className="text-xs text-white/30 mt-1">Live stats will appear when exams are in progress</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {liveStats.map((exam) => (
                <div
                  key={exam.exam_id}
                  className="border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] transition-colors bg-white/[0.02]"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-sm font-medium text-white/90 line-clamp-1">
                      {exam.exam_title}
                    </h3>
                    <span className="flex items-center gap-1 text-xs text-emerald-400 shrink-0 ml-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Live
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-white/40">Active</p>
                      <p className="text-lg font-bold text-white/90">{exam.active_students}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/40">Completed</p>
                      <p className="text-lg font-bold text-white/90">{exam.completed_students}</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/40">Violations</p>
                      <p className={`text-lg font-bold ${
                        exam.recent_violations > 0 ? 'text-red-400' : 'text-white/90'
                      }`}>
                        {exam.recent_violations}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-white/40">Progress</p>
                      <p className="text-lg font-bold text-white/90">
                        {(exam.average_progress * 100).toFixed(0)}%
                      </p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full transition-all"
                      style={{ width: `${Math.min(100, exam.average_progress * 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Section 6: Student Risk Profiles */}
      <Card>
        <div className="flex items-center justify-between p-6 pb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Student Risk Profiles</h2>
            <p className="text-sm text-white/50 mt-0.5">Students flagged based on violation patterns and risk scores</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-white/40">
            <AlertTriangle className="w-3.5 h-3.5 text-orange-400" />
            {riskProfiles.filter(
              (r) => r.risk_level === 'high' || r.risk_level === 'critical'
            ).length}{' '}
            high-risk students
          </div>
        </div>

        <div className="px-6 pb-6">
          {loadingRisk ? (
            <div className="h-32 flex items-center justify-center text-white/50">
              <RefreshCw className="w-5 h-5 animate-spin mr-2" />
              Loading risk profiles...
            </div>
          ) : riskProfiles.length === 0 ? (
            <div className="h-32 flex flex-col items-center justify-center text-white/40">
              <Users className="w-10 h-10 mb-2 opacity-40" />
              <p>No risk profile data available</p>
              <p className="text-xs text-white/30 mt-1">Student risk profiles are generated from proctoring violation data</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">Student</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">Total Violations</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">High Severity</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">Avg Risk Score</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-white/50 uppercase">Risk Level</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {riskProfiles.map((profile, idx) => {
                    const displayName = profile.student_name && profile.student_name !== 'Unknown'
                      ? profile.student_name
                      : `Student ${profile.student_id?.slice(0, 8) || idx + 1}`;

                    return (
                      <tr
                        key={profile.student_id || idx}
                        className="hover:bg-white/[0.02]"
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${
                              profile.risk_level === 'critical' ? 'bg-red-500/20 text-red-400' :
                              profile.risk_level === 'high' ? 'bg-orange-500/20 text-orange-400' :
                              profile.risk_level === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                              'bg-emerald-500/20 text-emerald-400'
                            }`}>
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-white/90 font-medium">{displayName}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-white/60">{profile.total_violations}</td>
                        <td className="py-3 px-4">
                          <span className={profile.high_severity_count > 0 ? 'text-red-400 font-medium' : 'text-white/60'}>
                            {profile.high_severity_count}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-white/[0.06] rounded-full h-1.5 overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  profile.average_risk_score >= 80 ? 'bg-red-500' :
                                  profile.average_risk_score >= 60 ? 'bg-orange-500' :
                                  profile.average_risk_score >= 40 ? 'bg-amber-500' :
                                  'bg-emerald-500'
                                }`}
                                style={{ width: `${Math.min(100, profile.average_risk_score)}%` }}
                              />
                            </div>
                            <span className="text-white/90 font-medium">
                              {(profile.average_risk_score || 0).toFixed(1)}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                              profile.risk_level === 'low'
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : profile.risk_level === 'medium'
                                ? 'bg-amber-500/20 text-amber-400'
                                : profile.risk_level === 'high'
                                ? 'bg-orange-500/20 text-orange-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}
                          >
                            {profile.risk_level.charAt(0).toUpperCase() + profile.risk_level.slice(1)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
